import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import SignIn from '@/components/SignIn.vue'
import EmailAuth from '@/components/EmailAuth.vue'
import Register from '@/components/Register.vue'
import Invite from '@/components/Invite.vue'
import Stats from '@/components/Stats.vue'
import Content from '@/components/Content.vue'
import Admin from '@/components/Admin.vue'
import StudentStats from '@/components/Stats/StudentStats.vue'
import GroupStats from '@/components/Stats/GroupStats.vue'
import StudentContent from '@/components/Content/StudentContent.vue'
import GroupContent from '@/components/Content/GroupContent.vue'

import Assignments from '@/components/Assignments.vue'
import ViewAssignment from '@/components/Assignments/ViewAssignment.vue'
import AssignmentsList from '@/components/Assignments/AssignmentsList.vue'
import AssignmentForm from '@/components/Assignments/AssignmentForm.vue'

import Organization from '@/components/Admin/Organization.vue'
import Account from '@/components/Admin/Account.vue'
import Purchases from '@/components/Admin/Purchases.vue'
import Integrations from '@/components/Admin/Integrations.vue'

import { userModule } from '@/store/user/module'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { isSupportWindow } from '@/utils'

const routes: RouteRecordRaw[] = [
    {
        path: '/stats',
        name: 'stats',
        component: Stats,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'group/:groupId/student/:studentId',
                name: 'student-stats',
                component: StudentStats,
            },
            {
                path: 'group/:groupId',
                name: 'group-stats',
                component: GroupStats,
            },
        ],
    },
    {
        path: '/content',
        name: 'content',
        component: Content,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'group/:groupId/student/:studentId',
                name: 'student-content',
                component: StudentContent,
            },
            {
                path: 'group/:groupId',
                name: 'group-content',
                component: GroupContent,
            },
        ],
    },
    {
        path: '/assignments',
        component: Assignments,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'assignments',
                component: AssignmentsList,
            },
        ],
    },
    {
        path: '/create-assignment',
        name: 'create-assignment',
        component: AssignmentForm,
        meta: { requiresAuth: true },
    },
    {
        path: '/assignment/:assignmentId',
        name: 'view-assignment',
        component: ViewAssignment,
        meta: { requiresAuth: true },
    },
    {
        path: '/assignment/:assignmentId/edit',
        name: 'edit-assignment',
        component: AssignmentForm,
        meta: { requiresAuth: true },
    },
    {
        path: '/admin',
        component: Admin,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'admin',
                redirect: { name: 'organization' },
            },
            {
                path: 'account',
                name: 'account',
                component: Account,
            },
            {
                path: 'integrations',
                name: 'integrations',
                component: Integrations,
            },
            {
                path: 'purchases',
                name: 'purchases',
                component: Purchases,
            },
            {
                path: 'organization',
                name: 'organization',
                component: Organization,
            },
        ],
    },
    {
        path: '/sign-in',
        name: 'sign-in',
        component: SignIn,
        alias: [ '/login' ],
    },
    {
        path: '/email-auth',
        name: 'email-auth',
        component: EmailAuth,
        alias: [ '/emailAuth' ],
    },
    {
        path: '/register',
        name: 'register',
        component: Register,
    },
    {
        path: '/invite',
        name: 'invite',
        component: Invite,
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: { name: 'stats', params: {} },
    },
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior (to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
                left: 0,
                top: 70,
            }
        }
        if (savedPosition) {
            return savedPosition
        }

        return { x: 0, y: 0 }
    },
    routes,
})

router.beforeEach(async (to, from, next) => {
    if (import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID && !isSupportWindow()) {
        gtag(
            'config',
            import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID,
            { 'page_path': '/' + to.path.slice(1) }
        )
    }

    const hasOrgExams = !!Object.keys(await examModule.actions.fetchCurrentOrgExams()).length
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    const currentUser = userModule.state.user
    const currentUserPendingOrgInstructor = instructorModule.getters.getCurrentUserPendingOrgInstructor()

    // If user is not signed in and route requires auth, completely sign out and redirect to sign-in
    // If user is signed in and has a pending invite, redirect to invite
    // If user is signed in and route does not require auth, redirect to stats
    // (NOTE: /register and /invite allow both signed in and not signed in users)
    // Otherwise, continue as usual
    if (requiresAuth && !currentUser) {
        next({ name: 'sign-in', query: { redirect: to.fullPath } })
    } else if (currentUser && currentUserPendingOrgInstructor && to.name !== 'invite') {
        next({ name: 'invite', query: { ...to.query } })
    } else if (requiresAuth && currentUser && !hasOrgExams) {
        // User exists but onboarding not complete yet
        next({ name: 'register' })
    } else if (!requiresAuth && currentUser && to.name !== 'register' && to.name !== 'invite') {
        next({ name: 'stats', query: { ...to.query } })
    } else {
        next()
    }
})

export {
    router,
    routes,
}
